import { useState, useEffect } from 'react';
import { getISTDateString } from '../lib/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { getRentDues, generateMonthlyRentDues, updateRentDue } from '../lib/api';
import { formatDate } from '../lib/dateUtils';
import { toast } from 'sonner';
import { CalendarDays, IndianRupee, RefreshCw, CheckCircle, Clock, AlertCircle, Phone, Users, XCircle } from 'lucide-react';

const StatusBadge = ({ status }) => {
  const variants = {
    pending: 'bg-amber-100 text-amber-700',
    partial: 'bg-blue-100 text-blue-700',
    paid: 'bg-emerald-100 text-emerald-700'
  };
  const labels = {
    pending: 'Pending',
    partial: 'Partial',
    paid: 'Paid'
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${variants[status] || 'bg-slate-100'}`}>
      {labels[status] || status}
    </span>
  );
};

export const RentDues = () => {
  const [dues, setDues] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [statusFilter, setStatusFilter] = useState('all');

  // Generate month options (last 12 months + next month)
  const monthOptions = [];
  const now = new Date();
  for (let i = -1; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    monthOptions.push({ value, label });
  }

  useEffect(() => {
    fetchDues();
  }, [selectedMonth, statusFilter]);

  const fetchDues = async () => {
    setLoading(true);
    try {
      const params = { month_year: selectedMonth };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const { data } = await getRentDues(params);
      setDues(data.dues || []);
      setSummary(data.summary || {});
    } catch (err) {
      toast.error('Failed to load rent dues');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDues = async () => {
    setGenerating(true);
    try {
      const { data } = await generateMonthlyRentDues(selectedMonth);
      toast.success(data.message || 'Rent dues generated');
      fetchDues();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate dues');
    } finally {
      setGenerating(false);
    }
  };

  const handleMarkPaid = async (due) => {
    try {
      await updateRentDue(due.id, {
        amount_paid: due.rent_amount,
        status: 'paid',
        payment_date: getISTDateString()
      });
      toast.success(`Marked ${due.guest_name}'s rent as paid`);
      fetchDues();
    } catch (err) {
      toast.error('Failed to update rent due');
    }
  };

  const handleMarkUnpaid = async (due) => {
    try {
      await updateRentDue(due.id, {
        amount_paid: 0,
        status: 'pending',
        payment_date: null
      });
      toast.success(`Reversed ${due.guest_name}'s payment - marked as pending`);
      fetchDues();
    } catch (err) {
      toast.error('Failed to reverse payment');
    }
  };

  const stats = [
    { label: 'Total Due', value: `₹${(summary.total_due || 0).toLocaleString()}`, icon: IndianRupee, color: 'bg-blue-100 text-blue-700', iconBg: 'bg-blue-600' },
    { label: 'Total Paid', value: `₹${(summary.total_paid || 0).toLocaleString()}`, icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700', iconBg: 'bg-emerald-600' },
    { label: 'Pending', value: `₹${(summary.total_pending || 0).toLocaleString()}`, icon: AlertCircle, color: 'bg-amber-100 text-amber-700', iconBg: 'bg-amber-600' },
    { label: 'Guests', value: summary.total_entries || 0, icon: Users, color: 'bg-purple-100 text-purple-700', iconBg: 'bg-purple-600' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rent-dues-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Rent Dues</h1>
          <p className="text-slate-500 mt-1">Manage monthly rent dues for all guests</p>
        </div>
        <div className="flex gap-3">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-[200px]" data-testid="month-filter">
              <CalendarDays className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={handleGenerateDues} disabled={generating} className="bg-blue-800 hover:bg-blue-900" data-testid="generate-dues-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
            Generate Dues
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, idx) => (
          <Card key={idx} className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500">{stat.label}</p>
                  <p className="text-2xl font-bold font-mono mt-1 tracking-tight">{stat.value}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.iconBg}`}>
                  <stat.icon className="h-5 w-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Status Summary */}
      <div className="flex gap-4">
        <Badge variant="outline" className="px-3 py-1">
          <Clock className="h-3 w-3 mr-1 text-amber-500" />
          Pending: {summary.pending_count || 0}
        </Badge>
        <Badge variant="outline" className="px-3 py-1">
          <AlertCircle className="h-3 w-3 mr-1 text-blue-500" />
          Partial: {summary.partial_count || 0}
        </Badge>
        <Badge variant="outline" className="px-3 py-1">
          <CheckCircle className="h-3 w-3 mr-1 text-emerald-500" />
          Paid: {summary.paid_count || 0}
        </Badge>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]" data-testid="status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Dues Table */}
      <Card data-testid="rent-dues-table">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <IndianRupee className="h-5 w-5" />
            Rent Dues - {monthOptions.find(m => m.value === selectedMonth)?.label}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {dues.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CalendarDays className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-lg font-medium">No rent dues for this month</p>
              <p className="text-sm">Click "Generate Dues" to create entries for all active guests</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Guest</TableHead>
                  <TableHead>Room</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Cycle</TableHead>
                  <TableHead className="text-right">Rent Amount</TableHead>
                  <TableHead className="text-right">Amount Paid</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dues.map((due) => (
                  <TableRow key={due.id} className={due.status === 'pending' ? 'bg-amber-50/50' : ''}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{due.guest_name}</p>
                        {due.mobile && (
                          <a href={`tel:${due.mobile}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {due.mobile}
                          </a>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{due.room_info}</TableCell>
                    <TableCell className="font-mono text-sm">{formatDate(due.due_date)}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${due.rent_cycle_type === 'standard' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                        {due.rent_cycle_type === 'standard' ? '1st' : 'Custom'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div>
                        <p className="font-mono font-bold">₹{due.rent_amount.toLocaleString()}</p>
                        {due.is_prorated && (
                          <p className="text-xs text-amber-600">Pro-rated</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-emerald-600">
                      ₹{(due.amount_paid || 0).toLocaleString()}
                    </TableCell>
                    <TableCell><StatusBadge status={due.status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        {due.status !== 'paid' ? (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleMarkPaid(due)}
                            className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                            data-testid={`mark-paid-${due.id}`}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Mark Paid
                          </Button>
                        ) : (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleMarkUnpaid(due)}
                            className="text-amber-600 border-amber-300 hover:bg-amber-50"
                            data-testid={`mark-unpaid-${due.id}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reverse
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
